import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { FileBarChart2, FileDown, FileSpreadsheet, Wrench, Euro, Percent } from "lucide-react";

const PT_MONTHS = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

function fmtEur(v) {
    return `${Number(v || 0).toFixed(2).replace(".", ",")} €`;
}

export default function RelatoriosPage() {
    const now = new Date();
    const [ano, setAno] = useState(now.getFullYear());
    const [mes, setMes] = useState(now.getMonth() + 1); // 1-12
    const [rep, setRep] = useState(null);
    const [loading, setLoading] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const r = await api.get("/admin/relatorio/mensal", { params: { ano, mes } });
            setRep(r.data);
        } catch (e) {
            toast.error("Erro a carregar relatório.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ano, mes]);

    async function download(kind) {
        try {
            const r = await api.get(`/admin/relatorio/mensal/${kind}`, {
                params: { ano, mes },
                responseType: "blob",
            });
            const blob = new Blob([r.data], {
                type: kind === "pdf" ? "application/pdf" : "text/csv;charset=utf-8",
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Relatorio-${ano}-${String(mes).padStart(2, "0")}.${kind}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Erro ao descarregar.");
        }
    }

    const anos = useMemo(() => {
        const cur = now.getFullYear();
        const out = [];
        for (let y = cur; y >= cur - 4; y -= 1) out.push(y);
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="space-y-8">
            <div className="flex flex-col gap-4 border-b border-zinc-200 pb-6 md:flex-row md:items-end md:justify-between">
                <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-zinc-500">
                        Backoffice · Contabilidade
                    </div>
                    <h1 className="mt-1 font-['Outfit'] text-3xl font-semibold tracking-tight text-zinc-900">
                        Relatórios mensais
                    </h1>
                    <p className="mt-1 text-sm text-zinc-600">
                        Resumo dos serviços concluídos ou entregues no mês, com IVA e top clientes.
                    </p>
                </div>
                <div className="flex flex-wrap items-end gap-3">
                    <div>
                        <Label className="text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-600">Ano</Label>
                        <Select value={String(ano)} onValueChange={(v) => setAno(Number(v))}>
                            <SelectTrigger className="mt-1 h-9 w-24 rounded-sm" data-testid="rel-ano">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {anos.map((y) => (
                                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-600">Mês</Label>
                        <Select value={String(mes)} onValueChange={(v) => setMes(Number(v))}>
                            <SelectTrigger className="mt-1 h-9 w-40 rounded-sm" data-testid="rel-mes">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {PT_MONTHS.map((n, i) => (
                                    <SelectItem key={i} value={String(i + 1)}>{n}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button
                        variant="outline"
                        className="h-9 rounded-sm"
                        onClick={() => download("csv")}
                        data-testid="btn-csv"
                    >
                        <FileSpreadsheet className="mr-2 h-4 w-4" /> CSV
                    </Button>
                    <Button
                        className="h-9 rounded-sm bg-[#FF3B30] hover:bg-[#D63026]"
                        onClick={() => download("pdf")}
                        data-testid="btn-pdf"
                    >
                        <FileDown className="mr-2 h-4 w-4" /> PDF
                    </Button>
                </div>
            </div>

            {loading || !rep ? (
                <div className="text-sm text-zinc-500">A calcular…</div>
            ) : (
                <>
                    {/* Cards */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <SummaryCard icon={Wrench} label="Serviços concluídos" value={rep.n_servicos} testid="stat-nservicos" />
                        <SummaryCard icon={Euro} label="Facturação (c/ IVA)" value={fmtEur(rep.total_faturado)} testid="stat-total" accent />
                        <SummaryCard icon={Percent} label={`IVA ${rep.iva_taxa}% incluído`} value={fmtEur(rep.total_iva)} testid="stat-iva" />
                    </div>

                    {/* Top clientes */}
                    <div>
                        <h2 className="mb-3 font-['Outfit'] text-lg font-semibold text-zinc-900">
                            Top clientes de {rep.mes_nome} {rep.ano}
                        </h2>
                        {rep.top_clientes.length === 0 ? (
                            <div className="border border-dashed border-zinc-200 bg-zinc-50 px-4 py-6 text-center text-sm text-zinc-500">
                                Sem clientes no período.
                            </div>
                        ) : (
                            <div className="border border-zinc-200 bg-white">
                                {rep.top_clientes.map((tc, i) => (
                                    <div
                                        key={i}
                                        className="flex items-center justify-between border-b border-zinc-100 px-4 py-2.5 last:border-0"
                                        data-testid={`top-cliente-${i}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="flex h-6 w-6 items-center justify-center rounded-sm bg-zinc-900 font-mono text-[11px] text-white">
                                                {i + 1}
                                            </span>
                                            <span className="font-medium text-zinc-900">{tc.cliente}</span>
                                            <span className="text-xs text-zinc-500">· {tc.n_servicos} serviço(s)</span>
                                        </div>
                                        <span className="font-mono text-sm font-semibold text-[#FF3B30]">{fmtEur(tc.total)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Detalhe */}
                    <div>
                        <h2 className="mb-3 font-['Outfit'] text-lg font-semibold text-zinc-900">Detalhe dos serviços</h2>
                        {rep.linhas.length === 0 ? (
                            <div className="border border-dashed border-zinc-200 bg-zinc-50 px-4 py-6 text-center text-sm text-zinc-500">
                                Sem serviços concluídos ou entregues neste mês.
                            </div>
                        ) : (
                            <div className="overflow-x-auto border border-zinc-200 bg-white">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-zinc-200 text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-500">
                                            <th className="px-3 py-2 text-left">Nº</th>
                                            <th className="px-3 py-2 text-left">Data</th>
                                            <th className="px-3 py-2 text-left">Cliente</th>
                                            <th className="px-3 py-2 text-left">Moto</th>
                                            <th className="px-3 py-2 text-left">Tipo</th>
                                            <th className="px-3 py-2 text-left">Estado</th>
                                            <th className="px-3 py-2 text-right">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody data-testid="linhas-tbody">
                                        {rep.linhas.map((ln, i) => (
                                            <tr key={i} className="border-b border-zinc-50 last:border-0 hover:bg-zinc-50">
                                                <td className="px-3 py-2 font-mono text-xs text-zinc-700">{ln.numero}</td>
                                                <td className="px-3 py-2 font-mono text-xs text-zinc-500">{ln.data}</td>
                                                <td className="px-3 py-2 text-zinc-900">{ln.cliente}</td>
                                                <td className="px-3 py-2 text-zinc-600">{ln.moto}</td>
                                                <td className="px-3 py-2 text-zinc-600">{ln.tipo}</td>
                                                <td className="px-3 py-2 text-xs text-zinc-500 capitalize">{ln.status}</td>
                                                <td className="px-3 py-2 text-right font-mono text-zinc-900">{fmtEur(ln.total)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

function SummaryCard({ icon: Icon, label, value, testid, accent }) {
    return (
        <div className="border border-zinc-200 bg-white p-5" data-testid={testid}>
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">
                <Icon className="h-3.5 w-3.5" />
                {label}
            </div>
            <div className={`mt-2 font-['Outfit'] text-3xl font-semibold ${accent ? "text-[#FF3B30]" : "text-zinc-900"}`}>
                {value}
            </div>
        </div>
    );
}
